import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import {
    Box,
    Toolbar,
    Typography,
    Tooltip,
    IconButton,
    InputAdornment,
    Fade,
    TextField,
} from '@mui/material';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SimpleMenu from '../menu';

// TODO jss-to-tss-react codemod: Unable to handle style definition reliably. Unsupported arrow function syntax.
//Unexpected value type of ConditionalExpression.
const useToolbarStyles = makeStyles()((theme) => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        gap: theme.spacing(1),
        minHeight: 56,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    highlight:
        theme.palette.mode === 'light'
            ? {
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.primary.main,
                borderBottom: `2px solid ${theme.palette.primary.dark}`,
                '& .MuiTypography-root': { fontWeight: 600 },
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.primary.dark,
                borderBottom: `2px solid ${theme.palette.primary.light}`,
                '& .MuiTypography-root': { fontWeight: 600 },
            },
    title: {
        flex: '1 1 auto',
        minWidth: 0,
        fontWeight: 600,
        fontSize: '1.125rem',
        color: theme.palette.text.primary,
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: theme.spacing(0.5),
        flexShrink: 0,
    },
    searchInput: {
        '& .MuiInput-input': {
            minWidth: 220,
        },
    },
}));

const calculateFilterMenu = (filters, selectedFilter, setSelectedFilter) => {
    var t_filterMenu = [];
    for (let i = 0; i < filters.length; i++) {
        t_filterMenu.push({
            label: filters[i],
            icon: selectedFilter === i ? <RadioButtonCheckedRoundedIcon color="primary" /> : <RadioButtonUncheckedRoundedIcon />,
            action: () => setSelectedFilter(i)
        });
    }
    return t_filterMenu;
}

CustomTableToolbar.propTypes = {
    title: PropTypes.string.isRequired,
    numSelected: PropTypes.number.isRequired,
};

export default function CustomTableToolbar(props) {
    const { classes, cx } = useToolbarStyles();
    const {
        title,
        numSelected,
        moreMenuItems,
        loadData,
        filters,
        selectedFilter,
        setSelectedFilter,
        showSearch,
        searchText,
        onSearch,
        handleShowSearch,
    } = props;
    const [anchorElMenuMas, setAnchorElMenuMas] = useState(null);

    const [anchorElMenuFilter, setAnchorElMenuFilter] = useState(null);
    const filterMenuItems = calculateFilterMenu(filters, selectedFilter, setSelectedFilter);

    const handleSearchBlur = (e) => {
        if (e.target.value === "")
            handleShowSearch(false);
    }

    const [searchTimer, setSearchTimer] = React.useState(null);
    const [inputText, setInputText] = React.useState(searchText);
    const onSearchInputChange = (e) => {
        setInputText(e.target.value);

        clearTimeout(searchTimer);
        setSearchTimer(setTimeout(() => {
            onSearch(e);
        }, 900));
    };

    const onCancelarBusqueda = (e) => {
        setInputText("");
        handleShowSearch(false);
    };

    return (
        <Toolbar
            className={cx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            {numSelected > 0 ?
                <Fragment>
                    <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
                        {numSelected} seleccionado{numSelected !== 1 ? 's' : ''}
                    </Typography>

                    <Tooltip title="Quitar selección o eliminar los seleccionados">
                        <IconButton aria-label="Eliminar seleccionados" size="large" color="inherit">
                            <DeleteRoundedIcon />
                        </IconButton>
                    </Tooltip>
                </Fragment>
                :
                <Fragment>
                    {!showSearch ? (
                        <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                            {title}
                        </Typography>
                    ) : (
                        <TextField
                            size="small"
                            placeholder="Escriba aquí para buscar en la tabla…"
                            value={inputText}
                            onBlur={(e) => handleSearchBlur(e)}
                            onChange={onSearchInputChange}
                            variant="outlined"
                            className={classes.searchInput}
                            sx={{ minWidth: 280, flex: '1 1 auto', maxWidth: 400 }}
                            autoFocus
                            inputProps={{ 'aria-label': 'Buscar en la tabla' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: inputText !== "" ? (
                                    <InputAdornment position="end">
                                        <Tooltip title="Borrar búsqueda y ver todos">
                                            <IconButton onClick={onCancelarBusqueda} color="primary" size="small" aria-label="Borrar búsqueda">
                                                <ClearRoundedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    )}

                    <Box className={classes.actions}>
                        {!showSearch && (
                            <Tooltip title="Buscar en la tabla (escriba y espere un momento)">
                                <IconButton color="primary" onClick={() => handleShowSearch(true)} size="large" aria-label="Abrir búsqueda">
                                    <SearchRoundedIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {loadData ? <Tooltip title="Recargar lista con los datos más recientes">
                            <IconButton color="primary" onClick={loadData} size="large" aria-label="Recargar tabla">
                                <ReplayRoundedIcon />
                            </IconButton>
                        </Tooltip> : null}

                        {filters && filters.length > 0 ? <Fragment>
                            <Tooltip title="Elegir un filtro para reducir los resultados">
                                <IconButton
                                    color="primary"
                                    onClick={(event) => setAnchorElMenuFilter(event.currentTarget)}
                                    size="large"
                                    aria-label="Abrir filtros">
                                    <FilterListRoundedIcon />
                                </IconButton>
                            </Tooltip>
                            <SimpleMenu
                                items={filterMenuItems}
                                anchorEl={anchorElMenuFilter}
                                setAnchorEl={setAnchorElMenuFilter}
                            />
                        </Fragment> : null}

                        {moreMenuItems ?
                            <Fragment>
                                <Tooltip title="Más opciones">
                                    <IconButton
                                        color="primary"
                                        onClick={(event) => setAnchorElMenuMas(event.currentTarget)}
                                        size="large"
                                        aria-label="Más opciones">
                                        <MoreVertRoundedIcon />
                                    </IconButton>
                                </Tooltip>
                                <SimpleMenu
                                    items={moreMenuItems}
                                    anchorEl={anchorElMenuMas}
                                    setAnchorEl={setAnchorElMenuMas}
                                />
                            </Fragment> : null}
                    </Box>
                </Fragment>
            }
        </Toolbar>
    );
};